import { isAxiosError } from 'axios'
import { AlertCircle, Building2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'

import { Skeleton } from '@/components/ui/skeleton'
import { FormPreview } from '@/features/form/components/FormPreview'
import { usePublicFormSchema, useSubmitPublicForm } from '@/hooks/usePublicForm'
import { uploadPublicFormFile } from '@/services/api/public-form.service'



type FormState = 'loading' | 'error' | 'form' | 'success' | 'redirecting'

const ERROR_TITLES: Record<number, string> = {
  404: 'Form not found',
  410: 'Form expired',
}

const ERROR_FALLBACK_BODY: Record<number, string> = {
  404: "This form link doesn't exist or has been removed.",
  410: 'This form is no longer accepting submissions.',
}

export function PublicFormPage() {
  const { token } = useParams<{ token: string }>()
  const { data: schema, isLoading, error } = usePublicFormSchema(token!)
  const { mutateAsync: submit, isPending: isSubmitting } = useSubmitPublicForm(token!)

  const [state, setState] = useState<FormState>('loading')
  const [countdown, setCountdown] = useState(3)

  useEffect(() => {
    if (isLoading) { setState('loading'); return }
    if (error)     { setState('error');   return }
    if (schema)    { setState('form');    return }
  }, [isLoading, error, schema])

  // Redirect countdown after success
  useEffect(() => {
    if (state !== 'redirecting') return
    if (countdown <= 0 && schema?.redirectUrl) {
      window.location.href = schema.redirectUrl
      return
    }
    const t = setTimeout(() => setCountdown(c => c - 1), 1000)
    return () => clearTimeout(t)
  }, [state, countdown, schema?.redirectUrl])

  const handleSubmit = async (
    values: Record<string, string>,
    submitterName: string,
    submitterEmail: string
  ) => {
    try {
      await submit({
        submitterName,
        submitterEmail,
        values: Object.entries(values).map(([formFieldId, value]) => ({ formFieldId, value })),
      })
      if (schema?.redirectUrl) {
        setState('redirecting')
      } else {
        setState('success')
      }
    } catch (err: unknown) {
      const body = isAxiosError(err) ? (err.response?.data as { message?: string } | undefined) : undefined
      const msg = body?.message || 'Submission failed. Please try again.'
      // Show form-level error via toast, but keep state as 'form'
      throw new Error(msg)
    }
  }

  const axiosStatus = isAxiosError(error) ? error.response?.status : undefined
  const axiosDetail = isAxiosError(error)
    ? (error.response?.data as { detail?: string } | undefined)?.detail
    : undefined
  const errTitle = axiosStatus ? ERROR_TITLES[axiosStatus] : undefined
  const errBody = axiosStatus
    ? (axiosDetail ?? ERROR_FALLBACK_BODY[axiosStatus])
    : undefined

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-50 via-white to-primary-50/30 flex flex-col">

      {/* Top brand bar */}
      <header className="border-b border-neutral-200 bg-white/80 backdrop-blur-sm">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-2">
          <div className="h-7 w-7 rounded-lg bg-primary-600 flex items-center justify-center">
            <Building2 className="h-4 w-4 text-white" />
          </div>
          <span className="text-sm font-semibold text-neutral-700">
            {schema?.workspaceName ?? 'Generic WorkFlow'}
          </span>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 flex items-start justify-center px-4 py-10">
        <div className="w-full max-w-lg">

          {state === 'loading' && (
            <div className="space-y-4">
              <Skeleton className="h-28 rounded-xl" />
              <Skeleton className="h-10 rounded-xl" />
              <Skeleton className="h-10 rounded-xl" />
              <Skeleton className="h-10 rounded-xl" />
            </div>
          )}

          {state === 'error' && (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 p-8 text-center">
              <div className="h-14 w-14 rounded-full bg-rose-100 flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="h-7 w-7 text-rose-500" />
              </div>
              <h2 className="text-lg font-bold text-rose-900">
                {errTitle ?? 'Something went wrong'}
              </h2>
              <p className="text-sm text-rose-700 mt-2">
                {errBody ?? 'This form is not available right now.'}
              </p>
            </div>
          )}

          {(state === 'form' || state === 'redirecting') && schema && (
            <div className="bg-white rounded-2xl border border-neutral-200 shadow-sm p-6">
              {state === 'redirecting' ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="h-16 w-16 rounded-full bg-success-100 flex items-center justify-center mb-4">
                    <span className="text-3xl">✓</span>
                  </div>
                  <h3 className="text-xl font-bold text-neutral-900 mb-2">Thank you!</h3>
                  <p className="text-sm text-neutral-500">
                    {schema.successMessage || 'Your response has been recorded.'}
                  </p>
                  <p className="text-xs text-neutral-400 mt-4">
                    Redirecting in {countdown}s…
                  </p>
                </div>
              ) : (
                <PublicFormSubmitter
                  schema={schema}
                  token={token!}
                  onSubmit={handleSubmit}
                  isSubmitting={isSubmitting}
                />
              )}
            </div>
          )}

          {state === 'success' && (
            <div className="bg-white rounded-2xl border border-neutral-200 shadow-sm p-6">
              <FormPreview
                fields={[]}
                workspaceName={schema?.workspaceName}
                title={schema?.title}
                description={schema?.description}
                success
                successMessage={schema?.successMessage}
              />
            </div>
          )}

        </div>
      </main>

      <footer className="py-4 text-center">
        <p className="text-[11px] text-neutral-400">Powered by Generic WorkFlow</p>
      </footer>
    </div>
  )
}

// ── Submitter sub-component ───────────────────────────────────────────────────

function PublicFormSubmitter({ schema, token, onSubmit }: {
  schema: NonNullable<ReturnType<typeof usePublicFormSchema>['data']>
  token: string
  onSubmit: (values: Record<string, string>, name: string, email: string) => Promise<void>
  isSubmitting: boolean
}) {
  const [submitError, setSubmitError] = useState<string | null>(null)
  // ?field_key=value in the link fills (and may hide) fields, e.g. ?utm_source=facebook
  const [searchParams] = useSearchParams()
  const prefill = Object.fromEntries(searchParams)

  const handleMockSubmit = async (
    values: Record<string, string>,
    submitterName: string,
    submitterEmail: string
  ) => {
    setSubmitError(null)
    try {
      await onSubmit(values, submitterName, submitterEmail)
    } catch (err: unknown) {
      setSubmitError(err instanceof Error ? err.message : 'Submission failed.')
      throw err // re-throw so FormPreview doesn't show success
    }
  }

  return (
    <div>
      {submitError && (
        <div className="mb-4 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 flex items-start gap-2">
          <AlertCircle className="h-4 w-4 text-rose-500 mt-0.5 shrink-0" />
          <p className="text-sm text-rose-700">{submitError}</p>
        </div>
      )}
      <FormPreview
        fields={schema.fields}
        workspaceName={schema.workspaceName}
        title={schema.title}
        description={schema.description}
        successMessage={schema.successMessage}
        prefill={prefill}
        showSubmitterFields
        onMockSubmit={handleMockSubmit}
        fileUploadConfig={schema.maxFileSizeBytes
          ? { maxFileSizeBytes: schema.maxFileSizeBytes, allowedTypes: schema.allowedFileTypes ?? [] }
          : null}
        onFileUpload={(field, file, options) =>
          uploadPublicFormFile(token, field.id, file, options)
        }
      />
    </div>
  )
}

